import { DateTime } from "luxon";
import { toPairs, map, sortBy, forEach } from "./lodash";
import { TIMEZONE_INFO } from "./tz";
import { RespondentModel } from "../models/RespondentModel";

export const EPOCH = DateTime.fromFormat("1979-12-31", "yyyy-MM-dd");

const applyConversionTable = (text: string, conversionTable: {}) => {
    const a = toPairs(conversionTable);
    const b = map(a, (p: string[], index: number) => ({ key: p[0], value: p[1], index }));
    const c = sortBy(b, (e: any) => -e.key.length);
    const d = forEach(c, (e: any) => (text = text.replace(e.key, `{{${e.index}}}`)));
    forEach(d, (e: any) => (text = text.replace(`{{${e.index}}}`, e.value)));
    return text;
};

/**
 * Convert .Net DateTime format to LuxonJS format. Anything not listed is the same between .Net and LuxonJS.
 *
 * .Net DateTime Format Reference: https://docs.microsoft.com/en-us/dotnet/standard/base-types/custom-date-and-time-format-strings
 *
 * LuxonJS Format Reference: https://github.com/moment/luxon/blob/master/docs/formatting.md
 * @param format .Net DateTime format
 */
export const cDateFormatToLuxon = (format: string) =>
    applyConversionTable(format, {
        ddd: "ccc", // Abbrev. name day of the week (Mon-Sun)
        dddd: "cccc", // Full name day of the week (Monday-Sunday)
        f: "uuu", // Tenths of a second (0-9)
        ff: "uu", // Hundreths of a second (00-99)
        fff: "u", // Milliseconds (000-999)
        g: "G", // Abbreviated era name
        y: "yy", // The year, from 0 to 99
        yy: "yy", // The year, from 00 to 99
        yyy: "yyyy", // The year, with a minimum of three digits.
        yyyy: "yyyy", // The year as a four-digit number.
        yyyyy: "yyyy", // The year as a five-digit number.
        gg: "G", // Abbreviated era name
        K: "ZZ", // Time zone information (+05:00)
        t: "a", // AM/PM (A/P)
        tt: "a", // AM/PM (AM/PM)
        z: "Z", // Hours offset (-7)
        zz: "ZZZ", // Hours offset (-07)
        zzz: "ZZ", // Hours offset (-07:00)
        ":": ":", // Time separator
        "/": "/", // Date separator
    });

// Docs:
//   https://docs.mobiscroll.com/javascript/calendar#localization-dateFormat
//   https://docs.mobiscroll.com/javascript/calendar#localization-timeFormat
export const cDateFormatToMobiscroll = (format: string) =>
    applyConversionTable(format, {
        "yyyy-MM-dd": "yy-mm-dd",
        "yy-MM-dd": "y-mm-dd",
        "dd/MM/yyyy": "dd/mm/yy",
        "dd/MM/yy": "dd/mm/y",
        "d/M/yy": "d/m/y",
        "MM/dd/yyyy": "mm/dd/yy",
        "MM/dd/yy": "m/dd/y",
        "dd.MM.yyyy": "dd.mm.yy",
        yyyyMMdd: "yymmdd",
        MMddyyyy: "mmddyy",
        "HH:mm": "HH:ii",
        "hh:mm tt": "hh:ii A",
        "HH-mm": "HH-ii",
        "hh-mm tt": "hh-ii A",
        hhmm: "hhii",
        HHmm: "HHii",
        hhmmss: "hhiiss",
        HHmmss: "HHiiss",
        "HH:mm:ss": "HH:ii:ss",
    });

export const timeZoneOffset = (respondent: RespondentModel) => {
    const timeZone = respondent && TIMEZONE_INFO[respondent.timeZone];
    if (timeZone) {
        if (DateTime.local().isInDST) return timeZone.offsetInMinutes + 60;
        return timeZone.offsetInMinutes;
    }
    return DateTime.local().offset;
};
